import { Injectable, EventEmitter } from "@angular/core";

import { CloudGatewayView, PrivateGatewayView, PrivateGateway, Gateway, Port } from "@impulse/sdp-shared-models";

import { ApiGatewayService } from "../../common/api-gateway/api-gateway.service";
import { environment } from "../../../environments/environment";
import { GatewayView } from "./gateway-view";
import { GatewayState } from "../../models";
import { GatewayDownloadFile } from "./models";
import { TranslateService } from "../../common/translate";

export interface GatewayUpdate {
	label: PrivateGateway.Label;
	dnsConfig: PrivateGateway.DnsConfig;
	publicHost: {
		ip: Gateway.Host;
		openVpnPort: Port.PortNumber;
		fwknopPort: Port.PortNumber;
	};
	privateHost: {
		ip: Gateway.Host;
	};
}

export interface IpAndPortsWithUuid {
	uuid: string;
	openvpnPort: Port.PortNumber;
	fwknopPort: Port.PortNumber;
	host: Gateway.Host;
}

export interface GatewayViews {
	cloudGateway: CloudGatewayView;
	gatewayViews: GatewayView[];
}

@Injectable()
export class GatewaysService {
	public reloadGateways: EventEmitter<GatewayViews> = new EventEmitter();
	public gatewayViews: GatewayView[] = [];
	public gatewayTypeSelect: EventEmitter<string> = new EventEmitter();
	public hasPending: EventEmitter<boolean> = new EventEmitter();

	constructor(
		private apiGatewayService: ApiGatewayService
	) { }

	public async checkForPendingGateway() {
		// this is used when other components call for pending explicitly
		const data = await this.apiGatewayService.getGateways("PENDING");
		this.hasPending.emit(data.privateGateways.length > 0);
		return data;
	}

	public async getGateways() {
		const data = await this.apiGatewayService.getGateways("ALL");
		this.doEmitters(data);
		return data;
	}

	public async editGateway(uuid: string, result: GatewayUpdate) {
		const data = await this.apiGatewayService.updateGateway(uuid, result);
		this.doEmitters(data);
		return getGatewayByUuid(data.privateGateways, uuid);
	}

	public async acceptPendingGateway(uuid: string) {
		const data = await this.apiGatewayService.acceptGateway(uuid);
		this.doEmitters(data);
		return getGatewayByUuid(data.privateGateways, uuid);
	}

	public async deleteGateway(uuid: string) {
		const data = await this.apiGatewayService.deleteGateway(uuid);
		this.doEmitters(data);
		return getGatewayByUuid(data.privateGateways, uuid);
	}

	public async activateGateway(uuid: string) {
		const data = await this.apiGatewayService.activateGateway(uuid);
		this.doEmitters(data);
		return getGatewayByUuid(data.privateGateways, uuid);
	}

	public async deactivateGateway(uuid: string) {
		const data = await this.apiGatewayService.deactivateGateway(uuid);
		this.doEmitters(data);
		return getGatewayByUuid(data.privateGateways, uuid);
	}

	private doEmitters(data: GatewayState) {
		const views = massageGateways(data);
		this.gatewayViews = views.gatewayViews;

		this.gatewayTypeSelect.emit(data.gatewayType);
		this.reloadGateways.emit(views);
		this.hasPending.emit(!!views.gatewayViews.find((g) => {
			return this.isGatewayPending(g);
		}));
	}

	public isGatewayPending(gateway: GatewayView) {
		return gateway.state === "pending" || gateway.state === "provisioning";
	}

	public async getAvailableVersion(): Promise<GatewayDownloadFile> {
		const dict = await this.apiGatewayService.getDownloadManifest(environment.GATEWAY_MANIFEST_URL);
		return dict["ova"]; // TODO OVA is currently the only supported format, so just grab that file
	}


	public getPublicHostAndPorts(hosts: PrivateGateway.HostAndPorts[]): PrivateGateway.HostAndPorts {
		return hosts.find((g) => {
			return g.type === "public";
		})!;
	}

	public getPrivateHostAndPorts(hosts: PrivateGateway.HostAndPorts[]): PrivateGateway.HostAndPorts {
		return hosts.find((g) => {
			return g.type === "private";
		})!;
	}

	public hasOverlappingIpPort(activeValue: IpAndPortsWithUuid, gateways: GatewayView[]) {
		const matchingGateways = gateways.filter((gatewayList) => {
			if (gatewayList.state === "active") {
				if (gatewayList.uuid === activeValue.uuid) {
					return false;
				}
				const gatewayListIpPorts = this.getPublicHostAndPorts(gatewayList.hostAndPorts);

				const hasMatchingIp = gatewayListIpPorts.host === activeValue.host;
				const hasMatchingPort = gatewayListIpPorts.fwknopPort === activeValue.fwknopPort ||
					gatewayListIpPorts.fwknopPort === activeValue.openvpnPort ||
					gatewayListIpPorts.openvpnPort === activeValue.fwknopPort ||
					gatewayListIpPorts.openvpnPort === activeValue.openvpnPort;
				return hasMatchingIp && hasMatchingPort;
			} else {
				return false;
			}
		});
		return matchingGateways.length > 0;
	}
}

function getGatewayByUuid(data: PrivateGatewayView[], uuid: string) {
	return data.find((g) => { return g.uuid === uuid; });
}

function massageGateways(data: GatewayState) {
	const gateways = data.privateGateways.map((gateway) => new GatewayView(gateway));
	const views: GatewayViews = { cloudGateway: data.cloudGateway, gatewayViews: gateways };
	return views;
}


